import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Setup() {
  const nav = useNavigate();
  const { user, setProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    birth_date: "", birth_time: "12:00", birth_place: "",
    birth_name: "", birth_time_certainty: "exact",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const steps = [
    { label: "When did you arrive?", key: "date" },
    { label: "The exact moment & place", key: "moment" },
    { label: "Your given name", key: "name" },
  ];

  const canNext = () => {
    if (step === 0) return !!form.birth_date;
    if (step === 1) return !!form.birth_place;
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      if (user) {
        const { data } = await api.post("/profiles", form);
        setProfile(data);
        toast.success("Your blueprint is ready");
        nav("/dashboard");
      } else {
        const { data } = await api.post("/preview/compute", form);
        sessionStorage.setItem("mynsight_preview", JSON.stringify({ ...data, form }));
        nav("/preview");
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen star-field flex items-center justify-center px-6 celestial">
      <div className="w-full max-w-md">
        <p className="overline text-center mb-3">Step {step + 1} of 3</p>
        <div className="flex gap-2 mb-10 justify-center">
          {steps.map((_, i) => <div key={i} className={`h-1 w-16 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-muted"}`} />)}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.4 }}>
            <h1 className="font-serif text-4xl mb-8 text-center">{steps[step].label}</h1>

            {step === 0 && (
              <input type="date" data-testid="input-birth-date" value={form.birth_date}
                onChange={(e) => set("birth_date", e.target.value)}
                className="w-full text-lg px-5 py-4 rounded-xl border border-border focus:border-primary outline-none" />
            )}

            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="overline block mb-2">Time of birth</label>
                  <input type="time" data-testid="input-birth-time" value={form.birth_time}
                    onChange={(e) => set("birth_time", e.target.value)}
                    className="w-full px-5 py-4 rounded-xl border border-border focus:border-primary outline-none" />
                  <div className="flex gap-2 mt-2">
                    {["exact", "approximate", "unknown"].map((c) => (
                      <button key={c} data-testid={`certainty-${c}`} onClick={() => set("birth_time_certainty", c)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.birth_time_certainty === c ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>{c}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="overline block mb-2">Place of birth</label>
                  <input type="text" placeholder="e.g. New York" data-testid="input-birth-place" value={form.birth_place}
                    onChange={(e) => set("birth_place", e.target.value)}
                    className="w-full px-5 py-4 rounded-xl border border-border focus:border-primary outline-none" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <input type="text" placeholder="Full birth name (optional)" data-testid="input-birth-name" value={form.birth_name}
                  onChange={(e) => set("birth_name", e.target.value)}
                  className="w-full px-5 py-4 rounded-xl border border-border focus:border-primary outline-none" />
                <p className="text-xs text-muted-foreground mt-3 text-center">Used for your numerology expression number.</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-10">
          <button data-testid="back-btn" disabled={step === 0} onClick={() => setStep((s) => s - 1)}
            className={`flex items-center gap-1 text-sm ${step === 0 ? "opacity-0 pointer-events-none" : "text-muted-foreground hover:text-foreground"}`}>
            <ArrowLeft size={16} /> Back
          </button>
          {step < 2 ? (
            <button data-testid="next-btn" disabled={!canNext()} onClick={() => setStep((s) => s + 1)}
              className="gold-btn px-7 py-3 rounded-full font-medium flex items-center gap-2 disabled:opacity-40">
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button data-testid="reveal-btn" disabled={loading} onClick={submit}
              className="gold-btn px-7 py-3 rounded-full font-medium flex items-center gap-2 disabled:opacity-60">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Reading the sky…</> : <>Reveal me <ArrowRight size={16} /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
